import styled, { css } from 'styled-components'
import { theme } from '@notino/react-styleguide'

// styles
import { NotinoLabelLarge, NotinoLabelLarge400, resetButtonStyles } from '../../../styles/helpers'
import { BREAKPOINTS } from '../../../styles/constants'

export const LayoutWrapper = styled.div<{ $isServiceDetailVisible: boolean }>`
	width: 100%;
	height: 100%;
	z-index: 1;

	${({ $isServiceDetailVisible }) =>
		$isServiceDetailVisible &&
		css`
			overflow: hidden;
			pointer-events: none;
			position: absolute;
			inset: 0;
		`}
`

export const ScrollButton = styled.button<{ $isMobileView: boolean }>`
	${resetButtonStyles};
	position: absolute;
	top: 8px;
	width: 24px;
	height: 24px;
	align-items: center;
	justify-content: center;
	display: none;

	${({ $isMobileView }) =>
		$isMobileView
			? css`
					svg {
						color: ${theme.color.text.inverse};
					}
				`
			: css`
					svg {
						color: ${theme.color.text.primary};
					}
				`}

	/* styles for devices with precise input (like mouse) */
	@media (pointer: fine) {
		display: flex;
	}
`

export const ScrollLeftButton = styled(ScrollButton)`
	left: 16px;

	&::after {
		left: 100%;
		background: linear-gradient(to right, white, transparent);
	}
`

export const ScrollRightButton = styled(ScrollButton)`
	right: 16px;

	&::after {
		right: 100%;
		background: linear-gradient(to left, white, transparent);
	}
`

export const MobileTabsWrapper = styled.div`
	margin: 0 -20px -20px -20px;

	@media (min-width: ${BREAKPOINTS.lg}) {
		display: none;
	}
`

export const DesktopTabsWrapper = styled.div`
	display: none;
	@media (min-width: ${BREAKPOINTS.lg}) {
		margin: -16px -24px 0 -24px;
		display: block;
	}
`

export const TabsContainer = styled.div`
	position: relative;
	isolation: isolate;
	width: 100%;

	/* styles for devices with precise input (like mouse) */
	@media (pointer: fine) {
		padding: 0 20px 20px 20px;
	}
`

export const Tabs = styled.div<{ $isScrollLeft?: boolean; $isScrollRight?: boolean }>`
	display: flex;
	scroll-behavior: smooth;
	z-index: 0;
	gap: 8px;
	overflow-x: auto;
	padding: 0 20px 20px 20px;

	/* styles for devices with precise input (like mouse) */
	@media (pointer: fine) {
		padding: 0;
		/* hide scrollbar */
		&::-webkit-scrollbar {
			display: none; // for Chrome, Safari and Opera
		}
		& {
			-ms-overflow-style: none; // for IE and Edge
			scrollbar-width: none; // for for Firefox
		}

		${({ $isScrollLeft, $isScrollRight }) => {
			if ($isScrollLeft && $isScrollRight) {
				return css`
					mask-image: linear-gradient(to right, transparent, black 20%, black 80%, transparent);
				`
			}

			if ($isScrollLeft) {
				return css`
					mask-image: linear-gradient(to left, black 80%, transparent);
				`
			}

			if ($isScrollRight) {
				return css`
					mask-image: linear-gradient(to right, black 80%, transparent);
				`
			}

			return null
		}}
	}

	@media (min-width: ${BREAKPOINTS.lg}) {
		padding: 0 20px;
		margin-bottom: 20px;
	}
`

export const TabButton = styled.button<{ $isMobileView: boolean }>`
	${resetButtonStyles};
	padding: 8px;
	${NotinoLabelLarge400};
	white-space: nowrap;
	border-bottom: 2px solid transparent;
	outline: 2px solid transparent;
	/* Inset so the ring is not clipped by the scrollable tablist overflow */
	outline-offset: -2px;

	&[aria-selected='true'] {
		position: relative;
		${NotinoLabelLarge};
	}

	&:focus-visible {
		outline-color: ${theme.color.border.selected};
	}

	${({ $isMobileView }) =>
		$isMobileView
			? css`
					color: ${theme.color.text.inverse};

					&[aria-selected='true'] {
						border-color: ${theme.color.common.white};
					}

					&:focus-visible {
						outline-color: ${theme.color.common.white};
					}
				`
			: css`
					color: ${theme.color.text.tertiary};

					&[aria-selected='true'] {
						color: ${theme.color.text.primary};
						border-color: ${theme.color.text.primary};
					}
				`}
`

export const TabsContent = styled.div`
	position: relative;
`

export const TabContent = styled.div<{ $isActive: boolean }>`
	position: relative;
	left: 0;
	top: 0;

	${({ $isActive }) =>
		!$isActive &&
		css`
			width: 0;
			height: 0;
			overflow: hidden;
		`}
`
